import React, { FunctionComponent, ReactElement, Ref } from 'react';

import {
  BUTTON_TYPES,
  Z_INDEX_OVERLAY,
} from '@savgroup-front-common/constants/shared';

import { SafeFormattedMessageWithoutSpread } from '../../formatters';
import { useFunctionOnEscape } from '../../hooks';
import { CrossIcon } from '../../protons/icons';
import { BackgroundDimmerNaked } from '../BackgroundDimmer';

import messages from './messages';
import { MODAL_SIZES } from './Modal.helpers';
import { useAriaHidden, useInitialPosition } from './Modal.hooks';
import {
  $CloseIconWrapper,
  $IconContainer,
  $ModalAnimationContainer,
  $ModalBlock,
  $ModalContainer,
} from './Modal.styles';

interface SubModalProps {
  shouldCloseOnDimmerClick: boolean;
  showCrossButton: boolean;
  shouldShowPadding: boolean;
  onClose?: () => void;
  icon?: ReactElement;
  dataTestId: string;
  size: MODAL_SIZES;
  forwardedRef?: Ref<HTMLDivElement>;
}

const SubModal: FunctionComponent<React.PropsWithChildren<SubModalProps>> = ({
  shouldCloseOnDimmerClick,
  showCrossButton,
  shouldShowPadding,
  dataTestId,
  children,
  onClose,
  size,
  icon,
  forwardedRef,
}) => {
  useFunctionOnEscape(onClose);
  useAriaHidden();
  const transformOrigin = useInitialPosition();

  return (
    <>
      <BackgroundDimmerNaked
        $zIndex={Z_INDEX_OVERLAY}
        $opacity={0.8}
        onClick={
          shouldCloseOnDimmerClick && onClose ? () => onClose() : undefined
        }
      />
      <$ModalContainer
        onClick={
          shouldCloseOnDimmerClick && onClose ? () => onClose() : undefined
        }
        $size={size}
      >
        <$ModalAnimationContainer
          $transformOrigin={transformOrigin}
          $size={size}
          onClick={
            shouldCloseOnDimmerClick && onClose ? () => onClose() : undefined
          }
          ref={forwardedRef}
        >
          <$ModalBlock
            shouldShowPadding={shouldShowPadding}
            data-testid={dataTestId}
            onClick={(event) => event.stopPropagation()}
            $size={size}
          >
            {icon && <$IconContainer>{icon}</$IconContainer>}
            {children}
            {showCrossButton && onClose && (
              <$CloseIconWrapper
                onClick={() => onClose()}
                type={BUTTON_TYPES.BUTTON}
                data-testid="modalClose"
              >
                <div>
                  <SafeFormattedMessageWithoutSpread message={messages.close} />
                  <CrossIcon size="18px" />
                </div>
              </$CloseIconWrapper>
            )}
          </$ModalBlock>
        </$ModalAnimationContainer>
      </$ModalContainer>
    </>
  );
};

SubModal.displayName = 'SubModal';

export default SubModal;
